import { useState, useCallback } from 'react';
import { useLinkGoogleAccount } from './useLinkGoogleAccount';
import { refreshAuthUser, getLinkedProviders } from './authSession';
import { syncProvisionedProfile } from '../services/authHelpers';
import { dismissStaffGoogleLinkOnboarding } from '../services/firebase/FunctionsService';

function mapLinkError(err: unknown, fallbackError: string, t?: Record<string, string>): string {
  const code = (err as { code?: string })?.code;
  if (code === 'auth/google-email-mismatch' && t?.googleEmailMismatch) {
    return t.googleEmailMismatch;
  }
  return (err as Error)?.message ?? fallbackError;
}

export function useStaffGoogleLinkWizard(
  onComplete: () => void,
  fallbackError = 'error',
  t?: Record<string, string>,
) {
  const { linkGoogle } = useLinkGoogleAccount();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const linkAndComplete = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await linkGoogle();
      try {
        await syncProvisionedProfile();
      } catch {
        // Auth may already have Google linked — do not block completion on sync.
        const providers = await getLinkedProviders().catch(() => []);
        const linked = providers.some((p) => p.id === 'google.com');
        if (!linked) throw new Error(fallbackError);
      }
      await refreshAuthUser();
      onComplete();
    } catch (err) {
      setError(mapLinkError(err, fallbackError, t));
    } finally {
      setBusy(false);
    }
  }, [linkGoogle, onComplete, fallbackError, t]);

  const skipAndComplete = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await dismissStaffGoogleLinkOnboarding();
      onComplete();
    } catch (err) {
      setError(mapLinkError(err, fallbackError, t));
    } finally {
      setBusy(false);
    }
  }, [onComplete, fallbackError, t]);

  return { busy, error, linkAndComplete, skipAndComplete };
}
